import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconLock } from '@tabler/icons-react';
import { AppModal } from '../../shared/components/AppModal';
import { ApiError } from '../../shared/api/problem';
import {
  listGroupPermissions,
  saveGroup,
  setGroupPermissions,
  type GroupDto,
} from './api';

interface Props {
  /** The group being edited, or null for a new one. */
  group: GroupDto | null;
  /** True when the dialog is creating rather than editing. */
  creating: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * A group, and the permissions it grants.
 *
 * This is the thing access control is actually made of: permissions attach to a group, a user is
 * given one or more groups, and the union is what they can do. Until tenant script 047 the map had
 * 105 seeded rows and no writer, so this dialog is the first way anybody could change it.
 *
 * PERMISSIONS ARE GROUPED BY CATEGORY, WITH A SELECT-ALL PER CATEGORY
 * Thirty-six checkboxes in one column is a wall. The categories are the ones the permissions
 * already carry — receiving, procurement, inventory — so the grouping is the server's, not a
 * second taxonomy invented here, and it lines up with how the menu is organised.
 *
 * A LOCKED GROUP IS READ-ONLY, AND SAYS WHY
 * Tenant Admin holds every permission and cannot be edited or deleted: a tenant that stripped it
 * would have no way to grant anything back, including the permission needed to repair it. The
 * server refuses it either way (53963); showing the state and the reason beats offering controls
 * that always fail.
 */
export function GroupPermissionsModal({ group, creating, onClose, onSaved }: Props) {
  const { t } = useTranslation();

  const [groupCode, setGroupCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [granted, setGranted] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const opened = group !== null || creating;
  const locked = group?.isLocked ?? false;

  const permissions = useQuery({
    queryKey: ['group-permissions', group?.groupId ?? 'none'] as const,
    queryFn: ({ signal }) => listGroupPermissions(group!.groupId, signal),
    enabled: group !== null,
  });

  useEffect(() => {
    if (!opened) return;
    setGroupCode(group?.groupCode ?? '');
    setDisplayName(group?.displayName ?? '');
    setErrors({});
    setSubmitError(null);
  }, [opened, group]);

  useEffect(() => {
    if (!permissions.data) return;
    setGranted(permissions.data.filter((p) => p.isGranted).map((p) => p.permissionId));
  }, [permissions.data]);

  /** The server's own categories, in the order it returned them. */
  const rows = permissions.data;
  const byCategory = useMemo(() => {
    const map = new Map<string, typeof rows>();
    for (const p of rows ?? []) {
      const list = map.get(p.category) ?? [];
      list.push(p);
      map.set(p.category, list);
    }
    return [...map.entries()];
  }, [rows]);

  const save = useMutation({
    mutationFn: async () => {
      const saved = await saveGroup(group?.groupId ?? null, {
        groupCode: groupCode.trim(),
        displayName: displayName.trim(),
      });
      // A brand new group has no permissions to send yet — the second call would be a no-op
      // against an empty set, and the dialog reopens on it to assign them.
      if (group) await setGroupPermissions(group.groupId, granted);
      return saved;
    },
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? (err.problem.detail ?? err.problem.title ?? t('groups.errors.saveFailed'))
          : t('groups.errors.saveFailed'),
      );
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const next: Record<string, string> = {};
    if (!groupCode.trim()) next.groupCode = t('groups.errors.codeRequired');
    if (!displayName.trim()) next.displayName = t('groups.errors.nameRequired');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    save.mutate();
  }

  function toggleCategory(ids: string[], on: boolean) {
    setGranted((current) =>
      on ? [...new Set([...current, ...ids])] : current.filter((id) => !ids.includes(id)),
    );
  }

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={creating ? t('groups.new') : (group?.displayName ?? '')}
      width="lg"
    >
      <form onSubmit={onSubmit} noValidate>
        <Stack gap="md">
          {locked && (
            <Alert color="gray" variant="light" icon={<IconLock size={16} />}>
              {t('groups.lockedNotice', { code: group?.groupCode })}
            </Alert>
          )}

          <Group grow align="flex-start">
            <TextInput
              label={t('groups.code')}
              description={t('groups.codeHint')}
              value={groupCode}
              onChange={(e) => setGroupCode(e.currentTarget.value.toUpperCase())}
              error={errors.groupCode}
              maxLength={50}
              disabled={locked}
              required
            />
            <TextInput
              label={t('groups.name')}
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              error={errors.displayName}
              maxLength={200}
              disabled={locked}
              required
            />
          </Group>

          {/* A new group is saved first, then reopened to assign permissions: it has no id to
              attach them to until it exists. */}
          {creating ? (
            <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
              {t('groups.permissionsAfterSave')}
            </Alert>
          ) : permissions.isLoading ? (
            <Center py="xl">
              <Loader color="invetrak" />
            </Center>
          ) : (
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500}>{t('groups.permissionsSection')}</Text>
                <Badge variant="light" color={granted.length === 0 ? 'yellow' : 'invetrak'}>
                  {t('groups.grantedCount', { count: granted.length })}
                </Badge>
              </Group>

              {granted.length === 0 && !locked && (
                <Alert color="yellow" variant="light" icon={<IconAlertCircle size={16} />}>
                  {t('groups.grantsNothingWarning')}
                </Alert>
              )}

              {byCategory.map(([category, perms]) => {
                const ids = (perms ?? []).map((p) => p.permissionId);
                const allOn = ids.every((id) => granted.includes(id));
                return (
                  <Stack key={category} gap={4}>
                    <Group gap="xs">
                      <Text size="sm" fw={600} tt="capitalize">
                        {category}
                      </Text>
                      {!locked && (
                        <Anchor
                          component="button"
                          type="button"
                          size="xs"
                          onClick={() => toggleCategory(ids, !allOn)}
                        >
                          {allOn ? t('groups.selectNone') : t('groups.selectAll')}
                        </Anchor>
                      )}
                    </Group>
                    {(perms ?? []).map((p) => (
                      <Checkbox
                        key={p.permissionId}
                        label={p.displayName}
                        description={p.permissionCode}
                        checked={granted.includes(p.permissionId)}
                        disabled={locked}
                        onChange={(e) =>
                          setGranted((current) =>
                            e.currentTarget.checked
                              ? [...current, p.permissionId]
                              : current.filter((id) => id !== p.permissionId),
                          )
                        }
                      />
                    ))}
                  </Stack>
                );
              })}
            </Stack>
          )}

          {submitError && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {locked ? t('shell.close') : t('shell.cancel')}
            </Button>
            {!locked && (
              <Button type="submit" color="invetrak" loading={save.isPending}>
                {creating ? t('groups.create') : t('shell.saveChanges')}
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </AppModal>
  );
}
